import React from "react";
import { AlertTriangle } from "lucide-react";
import { col, ENGINE_TONE, FAMILY_TONE, nActions } from "../../lib/opportunityApi";
import { formatPeso } from "../../lib/utils";

const php = (v) => formatPeso(v, { compact: true });

function EngineSplit({ engines }) {
  return (
    <div className="space-y-3">
      <div className="flex h-3 w-full rounded-sm overflow-hidden bg-surface-2" data-testid="opp-engine-bar">
        {engines.map(e => (
          <span key={e.engine} title={`${e.label} · ${php(e.value)} · ${e.share}%`} style={{ width: `${e.share}%`, background: col(ENGINE_TONE[e.engine] || "primary", 0.85) }} />
        ))}
      </div>
      <ul className="divide-y divide-border border border-border rounded-sm">
        {engines.map(e => (
          <li key={e.engine} data-testid={`opp-engine-${e.engine}`} className="px-3 py-2.5 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3">
            <div className="min-w-0 flex items-center gap-2 text-[12.5px] font-medium">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: col(ENGINE_TONE[e.engine] || "primary") }} />
              <span className="truncate">{e.label}</span>
            </div>
            <div className="font-mono-tabular text-[12.5px] font-semibold text-right">{php(e.value)} <span className="text-muted font-normal">· {e.share}%</span></div>
            <div className="text-[11px] text-muted leading-snug mt-0.5 pl-4">{nActions(e.count)} · {e.basis}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Families({ families }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="opp-families">
      {families.map(fm => (
        <div key={fm.family} data-testid={`opp-family-${fm.family}`} className="border rounded-sm px-3 py-2.5" style={{ borderColor: col(FAMILY_TONE[fm.family], 0.4) }}>
          <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: col(FAMILY_TONE[fm.family]) }}>{fm.label}</div>
          <div className="font-mono-tabular text-[15px] font-semibold mt-0.5">{php(fm.value)} <span className="text-[11px] text-muted font-normal">· {fm.share}% · {nActions(fm.count)}</span></div>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {fm.actions.map(a => <span key={`${a.engine}-${a.action}`} title={a.basis} className="text-[10.5px] px-1.5 py-0.5 border border-border rounded-sm bg-surface-2">{a.action} {php(a.value)}</span>)}
          </div>
        </div>
      ))}
    </div>
  );
}

function WatchOuts({ f }) {
  const o = f.overlap;
  const buy = f.families.find(x => x.family === "buy");
  const low = f.confidence.find(x => x.tier === "low");
  const items = [
    o.pairs_with_2plus_engines > 0 && `${php(o.value_on_those_pairs)} (${o.share}%) sits on ${o.pairs_with_2plus_engines} SKU × branch pairs that two or more engines scored — the same stock can count more than once.`,
    buy && `${php(buy.value)} is purchase orders to place — spend, not a saving.`,
    low && `${php(low.value)} (${low.share}%) comes from low-confidence actions.`,
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <div data-testid="opp-watchouts" className="border border-warning/40 bg-warning/5 rounded-sm px-3.5 py-3 space-y-1.5">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-warning inline-flex items-center gap-1.5"><AlertTriangle size={11} /> Read the total with care</div>
      {items.map((t, i) => <p key={i} data-testid={`opp-watchout-${i}`} className="text-[12.5px] leading-relaxed">{t}</p>)}
    </div>
  );
}

// How the total is built: by engine, by type of action, by approval state and confidence.
export function OpportunityBuild({ f }) {
  return (
    <section data-testid="opp-build" className="space-y-4">
      <h3 className="font-display text-[15px] font-semibold tracking-tight">How the total is built</h3>
      <EngineSplit engines={f.engines} />
      <div className="kpi-label text-[10px]">By type of action</div>
      <Families families={f.families} />
      <div className="flex flex-wrap gap-1.5 text-[11px]" data-testid="opp-status">
        {f.status.map(s => <span key={s.state} className="px-1.5 py-0.5 border border-border rounded-sm capitalize">{s.count} {s.state} · {php(s.value)}</span>)}
        {f.confidence.map(c => <span key={c.tier} className="px-1.5 py-0.5 border border-border rounded-sm text-muted">{c.tier} confidence {c.share}%</span>)}
      </div>
      <WatchOuts f={f} />
    </section>
  );
}
